import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { INVENTORY_STATE_OPTIONS, SALE_STATE_OPTIONS } from "../../../components/custom/merchandise/constants";
import { toTitleCase } from "../../../utils/formatText";
import { resolveFilterOption } from "../../merchandising/components/tables/resolveFilterOption";

export const ENROLL_OPTIONS = [
  {
    value: "all",
    label: ($) => $.advertising["All Enrollment States"],
  },
  {
    value: "enabled",
    label: ($) => $.advertising["Enrolled Only"],
  },
  {
    value: "not_enrolled",
    label: ($) => $.advertising["Not Enrolled Only"],
  },
  {
    value: "disabled",
    label: ($) => $.advertising["Paused Only"],
  },
];

export const PRODUCT_AD_SERVING_STATUS_OPTIONS = [
  {
    value: "AD_STATUS_LIVE",
    label: ($) => $.advertising["Serving"],
  },
  {
    value: "NOT_BUYABLE",
    label: ($) => $.advertising["Not Buyable"],
  },
  {
    value: "MISSING_DECORATION",
    label: ($) => $.advertising["Missing Decoration"],
  },
  {
    value: "CAMPAIGN_PAUSED",
    label: ($) => $.advertising["Campaign Paused"],
  },
  {
    value: "AD_POLICING_SUSPENDED",
    label: ($) => $.advertising["Ad Policing Suspended"],
  },
  { value: "AD_PAUSED", label: ($) => $.advertising["Ad Paused"] },
  { value: "null", label: ($) => $.advertising["Pending"] },
  { value: "INELIGIBLE", label: ($) => $.advertising["Ineligible"] },
  {
    value: "CAMPAIGN_OUT_OF_BUDGET",
    label: ($) => $.advertising["Campaign Out of Budget"],
  },
  {
    value: "PORTFOLIO_OUT_OF_BUDGET",
    label: ($) => $.advertising["Portfolio Out of Budget"],
  },
  {
    value: "ACCOUNT_OUT_OF_BUDGET",
    label: ($) => $.advertising["Account Out of Budget"],
  },
];

export const INVENTORY_STATE_FILTER = {
  name: ($) => $.advertising["Inventory State"],
  id: "inventory_state",
  format: "select",
  selectOptions: [
    {
      value: "all",
      label: ($) => $.advertising["All Inventory States"],
    },
    ...INVENTORY_STATE_OPTIONS,
  ],
  override: true,
  defaultValue: "all",
  operator: "custom",
  filterFunction: (product, filterValue) => {
    if (filterValue !== "all") {
      if (filterValue === "IN_STOCK") {
        return product?.inventory_state === filterValue && product?.stock_status === filterValue;
      }
      return (
        product?.inventory_state === filterValue ||
        product?.stock_status === filterValue ||
        (product?.stock_status === "IN_STOCK_SCARCE" && filterValue === "LOW_STOCK")
      );
    }
    return true;
  },
};

export const SALES_STATE_FILTER = {
  name: ($) => $.advertising["Sales State"],
  id: "state",
  format: "select",
  selectOptions: [
    {
      value: "all",
      label: ($) => $.advertising["All Sales States"],
    },
    ...SALE_STATE_OPTIONS,
  ],
  override: true,
  defaultValue: "all",
  operator: "custom",
  filterFunction: (product, filterValue) => {
    if (filterValue !== "all") {
      return product?.sales_state === filterValue;
    }
    return true;
  },
};

export const PRODUCT_WITH_ADS_FILTER = {
  name: ($) => $.advertising["Advertising Status"],
  id: "ad_cnt",
  format: "select",
  selectOptions: [
    {
      value: "all",
      label: ($) => $.advertising["All Products"],
    },
    {
      value: "withActiveAds",
      label: ($) => $.advertising["Active Ads"],
    },
    {
      value: "withoutActiveAds",
      label: ($) => $.advertising["No Active Ads"],
    },
  ],
  override: true,
  defaultValue: "all",
  operator: "custom",
  filterFunction: (product, filterValue) => {
    if (filterValue === "withActiveAds") {
      return product?.active_ad_count;
    }
    if (filterValue === "withoutActiveAds") {
      return !product?.active_ad_count;
    }

    return true;
  },
};

export const PRODUCT_AD_STATUS_FILTER = {
  name: ($) => $.advertising["Serving Status"],
  id: "serving_status",
  format: "select",
  selectOptions: [
    {
      value: "all",
      label: ($) => $.advertising["All Statuses"],
    },
    ...PRODUCT_AD_SERVING_STATUS_OPTIONS,
  ],
  override: true,
  defaultValue: "all",
  operator: "custom",
  filterFunction: (product, filterValue) => {
    if (filterValue !== "all") {
      return product?.serving_status === filterValue;
    }
    return true;
  },
};
export const PRICING_PRODUCT_ENROLMENT_FILTER = {
  name: ($) => $.advertising["Enrollment State"],
  id: "enrolled",
  format: "select",
  selectOptions: ENROLL_OPTIONS,
  override: true,
  defaultValue: "all",
  operator: "custom",
  filterFunction: (product, filterValue) => {
    switch (filterValue) {
      case "all":
        return true;
      default:
        return product.pricing_plan__status === filterValue;
    }
  },
};

const buildProductVariantsFilter = (variants) => {
  const variantOptions = [];

  if (variants) {
    Object.keys(variants).forEach((variant) => {
      const vr = variants[variant];
      for (let i = 0; i < vr.length; i++) {
        const v = vr[i];
        const key = `${variant} - ${v}`;
        variantOptions.push({
          value: key,
          label: toTitleCase(key),
        });
      }
    });
  }

  return {
    name: ($) => $.advertising["Variants"],
    id: "variants",
    format: "select",
    selectOptions: [
      {
        value: "all",
        label: ($) => $.advertising["All Variants"],
      },
      ...variantOptions.flat(),
    ],
    override: true,
    defaultValue: "all",
    operator: "custom",
    filterFunction: (product, filterValue) => {
      if (filterValue === "all") {
        return true;
      }

      if (!product?.variations) {
        return false;
      }
      const variations = product.variations;
      if (!variations) {
        return false;
      }
      const parts = filterValue?.split("-");
      const key = parts?.[0].trim().toLocaleLowerCase();
      if (!key) {
        return true;
      }
      const value = parts?.splice(1, parts.length).join("-").trim();

      if (variations[key] !== undefined && variations[key].toString() === value.toString()) {
        return true;
      }
      return false;
    },
  };
};

// Resolves the advertising-namespace selectors carried by the filter defs above
// into plain strings, bound to the `advertising` namespace at the call site so
// the generic FilterWidget never has to resolve them. See resolveFilterOption.
export function useProductLifecycleFilters() {
  const { t } = useTranslation(["advertising"]);
  return useMemo(
    () => ({
      INVENTORY_STATE_FILTER: resolveFilterOption(t, INVENTORY_STATE_FILTER),
      SALES_STATE_FILTER: resolveFilterOption(t, SALES_STATE_FILTER),
      PRODUCT_WITH_ADS_FILTER: resolveFilterOption(t, PRODUCT_WITH_ADS_FILTER),
      PRODUCT_AD_STATUS_FILTER: resolveFilterOption(t, PRODUCT_AD_STATUS_FILTER),
      PRICING_PRODUCT_ENROLMENT_FILTER: resolveFilterOption(t, PRICING_PRODUCT_ENROLMENT_FILTER),
      buildProductVariantsFilter: (variants) => resolveFilterOption(t, buildProductVariantsFilter(variants)),
      buildCustomTagsFilter: (tags) => resolveFilterOption(t, buildCustomTagsFilter(tags)),
    }),
    [t],
  );
}

const buildCustomTagsFilter = (tags) => {
  const tagOptions = [
    ...new Set(
      tags
        // Filter out potential null or empty string values
        // Some walmart products have tags set to null instead of empty array
        ?.filter((tag) => tag) ?? [],
    ),
  ]?.map((tag) => ({
    value: tag.toLowerCase(),
    label: tag,
  }));

  return {
    name: ($) => $.advertising["Custom Tags"],
    id: "tags",
    format: "multi-select",
    selectOptions: [
      {
        value: "all",
        label: ($) => $.advertising["All Tags"],
      },
      ...tagOptions.flat(),
    ],
    override: true,
    defaultValue: "all",
    operator: "custom",
    filterFunction: (entity, filterValue) => {
      if (filterValue === "all") {
        return true;
      }

      return entity?.tags?.map((tag) => tag.toLowerCase())?.includes(filterValue?.toLowerCase());
    },
  };
};
